/**
 * App.Selection - 选中拾取 / 属性面板 / 选中高亮
 */

import * as THREE from 'three';
import type { Cell, Selection as SelType, Room, End, Shaft } from '../types';
import { TileType, SelectionKind } from '../types';
import { App } from './App';

/* ─────────────── Module: Selection（选中拾取 / 属性面板 / 选中高亮）─────────────── */
App.Selection = {
  /** 当前选中对象 */
  sel: null as SelType | null,

  /**
   * 由给定格子反查对象
   */
  pick(cell: Cell): SelType | null {
    if (!cell) return null;

    const f = App.Store.f;
    if (!f) return null;

    const end = f.ends.find(e => e.x === cell.x && e.z === cell.z);
    if (end) {
      return { kind: SelectionKind.END, ref: end, cell };
    }

    const room = (App.World as any).roomAtCell(cell);
    if (room) {
      return { kind: SelectionKind.ROOM, ref: room, cell };
    }

    const T = App.Config?.T || { RISER: 4 };
    if (f.type[cell.x]?.[cell.z] === T.RISER) {
      const shaft = (App.World as any).shaftAtCell(cell.x, cell.z) as Shaft | null;
      if (shaft) return { kind: SelectionKind.SHAFT, ref: shaft, cell };
    }

    return { kind: SelectionKind.CELL, ref: null, cell };
  },

  /**
   * 通过格子选中
   */
  selectByCell(cell: Cell): void {
    const o = this.pick(cell);
    if (!o) return;
    this.sel = o;
    this.render();
    this.drawOutline();
  },

  /**
   * 属性模式点击：优先 raycast 直接命中末端柱
   */
  selectFromEvent(ev: Event): void {
    const I = App.Interaction;
    const endMesh = (I as any).raycastEnd(ev);

    if (endMesh && endMesh.userData.end) {
      const e = endMesh.userData.end;
      this.sel = { kind: SelectionKind.END, ref: e, cell: { x: e.x, z: e.z } };
      this.render();
      this.drawOutline();
      return;
    }

    const cell = (I as any).cellFromEvent(ev);
    if (cell) this.selectByCell(cell);
  },

  /**
   * 清空选中
   */
  clear(): void {
    this.sel = null;
    this.render();
    this.drawOutline();
  },

  /**
   * 撤销/重做后更新选中
   */
  afterChange(): void {
    if (this.sel && this.sel.cell) {
      this.sel = this.pick(this.sel.cell);
    }
    this.drawOutline();
    this.render();
  },

  /**
   * 绘制选中轮廓
   */
  drawOutline(): void {
    const RR = App.Render;
    const W = App.World;

    RR.gSel.clear();
    if (!this.sel) return;

    const o = this.sel;

    if (o.kind === SelectionKind.CELL || o.kind === SelectionKind.SHAFT) {
      const w = W.g2w(o.cell.x, o.cell.z);

      const box = new THREE.Mesh(
        new THREE.BoxGeometry(0.98, 1.6, 0.98),
        new THREE.MeshBasicMaterial({
          color: 0x3df0ff,
          transparent: true,
          opacity: 0.18,
          depthWrite: false,
        })
      );
      box.position.set(w.x, w.y + 0.8, w.z);
      RR.gSel.add(box);

      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(0.98, 1.6, 0.98)),
        new THREE.LineBasicMaterial({ color: 0x3df0ff })
      );
      edges.position.set(w.x, w.y + 0.8, w.z);
      RR.gSel.add(edges);
    } else {
      // room / end：遍历其格子画高亮框
      let cells: Cell[];
      if (o.kind === SelectionKind.ROOM) {
        cells = (W as any).roomCells(o.ref as Room);
      } else {
        cells = [{ x: (o.ref as End).x, z: (o.ref as End).z }];
      }

      const eg = new THREE.EdgesGeometry(
        new THREE.BoxGeometry(0.98, o.kind === SelectionKind.ROOM ? 2.0 : 1.6, 0.98)
      );

      for (const c of cells) {
        const w = W.g2w(c.x, c.z);
        const e = new THREE.LineSegments(eg, new THREE.LineBasicMaterial({ color: 0x3df0ff }));
        e.position.set(w.x, w.y + (o.kind === SelectionKind.ROOM ? 1.0 : 0.8), w.z);
        RR.gSel.add(e);
      }
    }
  },

  /**
   * 渲染属性面板
   */
  render(): void {
    const sec = document.getElementById('propSection');
    if (!sec) return;

    const o = this.sel;
    const T = App.Config?.T || { WALK: 0, FORBID: 1, ROOM: 2, END: 3, RISER: 4 };
    const f = App.Store.f;
    const S = App.Store;

    if (!o) {
      sec.innerHTML = '<div class="propEmpty">进入「属性」模式 · 点击机房 / 末端 / 格子查看属性</div>';
      return;
    }

    if (o.kind === SelectionKind.END) {
      const e = o.ref as End;
      const res = S.results.find((r: any) => r.end === e);
      const binds = (App.Routing as any).boundRoomIds(e.id);
      const hex = '#' + (e.color as number).toString(16).padStart(6, '0');

      sec.innerHTML =
        `<div class="propTitle"><span class="swatch" style="background:${hex}"></span>${e.id}${this._abbr(e.systemId)}</div>` +
        this._row('类型', '末端（终点）') +
        this._row('所在楼层', (App.World as any).floorName(e.floorIdx)) +
        this._row('坐标', `${e.x}, ${e.z}`) +
        this._row('管线系统', this._systemSelect(e.systemId), true) +
        this._row('是否自建', e.added ? '是' : '否') +
        this._row('路径长度', res && res.ok ? res.len + ' m' : '—') +
        this._row('折点数', res && res.ok ? res.cor : '—') +
        this._row(
          '状态',
          res
            ? res.ok
              ? '✓ 连通'
              : res.reason === '未绑定机房'
              ? '× 未绑定'
              : '× 隔离'
            : '未寻路'
        ) +
        this._row('绑定机房', binds.length ? binds.join(', ') : S.bindMode ? '（未绑定）' : '自动模式') +
        this._row(
          '颜色',
          `<span class="propColor"><input type="color" id="propColor" value="${hex}"></span>`,
          true
        );

      const ci = document.getElementById('propColor');
      if (ci) {
        (ci as HTMLInputElement).onchange = (ev: Event) =>
          (App.Interaction as any).setObjColor(e, (ev.target as HTMLInputElement).value);
      }
      const si = document.getElementById('propSystem');
      if (si) {
        (si as HTMLSelectElement).onchange = (ev: Event) =>
          (App.Interaction as any).setObjSystem(e, +(ev.target as HTMLSelectElement).value);
      }
    } else if (o.kind === SelectionKind.ROOM) {
      const r = o.ref as Room;
      const shapeName = r.shape.length === 6 ? '2×3（主机房）' : '2×2（自建）';
      const set = (App.Routing as any).boundSetFor(r.id);
      const connectedEnds = [...set];
      const hex = '#' + (r.color as number).toString(16).padStart(6, '0');

      sec.innerHTML =
        `<div class="propTitle"><span class="swatch" style="background:${hex}"></span>${r.id}${this._abbr(r.systemId)}</div>` +
        this._row('类型', '设备机房（源点）') +
        this._row('所在楼层', (App.World as any).floorName(r.floorIdx)) +
        this._row('锚点坐标', `${r.x}, ${r.z}`) +
        this._row('形状', shapeName) +
        this._row('管线系统', this._systemSelect(r.systemId), true) +
        this._row('是否自建', r.added ? '是' : '否') +
        this._row(
          '连接末端',
          S.bindMode ? (connectedEnds.length ? connectedEnds.join(', ') : '（未绑定）') : '自动模式'
        ) +
        this._row(
          '颜色',
          `<span class="propColor"><input type="color" id="propColor" value="${hex}"></span>`,
          true
        );

      const ci = document.getElementById('propColor');
      if (ci) {
        (ci as HTMLInputElement).onchange = (ev: Event) =>
          (App.Interaction as any).setObjColor(r, (ev.target as HTMLInputElement).value);
      }
      const si = document.getElementById('propSystem');
      if (si) {
        (si as HTMLSelectElement).onchange = (ev: Event) =>
          (App.Interaction as any).setObjSystem(r, +(ev.target as HTMLSelectElement).value);
      }
    } else if (o.kind === SelectionKind.SHAFT) {
      const shaft = o.ref as Shaft;
      const fromName = S.floors[shaft.floorFrom]?.name || '—';
      const toName = S.floors[shaft.floorTo]?.name || '—';
      const span = shaft.floorTo - shaft.floorFrom + 1;

      const floorOpts = (sel: number) =>
        S.floors.map((fl: any, i: number) => `<option value="${i}"${i === sel ? ' selected' : ''}>${fl.name}</option>`).join('');

      const sysHex = '#' + ((App.Routing as any).systemColorOf(shaft.systemId, 0x2ecc71) as number).toString(16).padStart(6, '0');

      sec.innerHTML =
        `<div class="propTitle"><span class="swatch" style="background:${sysHex}"></span>井${shaft.id}${this._abbr(shaft.systemId)}</div>` +
        this._row('类型', '竖井（跨层立管）') +
        this._row('编号', '井' + shaft.id) +
        this._row('位置', `${shaft.x}, ${shaft.z}`) +
        this._row('管线系统', this._systemSelect(shaft.systemId), true) +
        this._row(
          '起始楼层',
          `<select id="propShaftFrom" style="width:100px;background:#1a2440;color:var(--txt);border:1px solid var(--line);border-radius:5px;padding:3px">${floorOpts(shaft.floorFrom)}</select>`,
          true
        ) +
        this._row(
          '终止楼层',
          `<select id="propShaftTo" style="width:100px;background:#1a2440;color:var(--txt);border:1px solid var(--line);border-radius:5px;padding:3px">${floorOpts(shaft.floorTo)}</select>`,
          true
        ) +
        this._row('当前贯穿', `${fromName} ~ ${toName}（共 ${span} 层）`);

      const sf = document.getElementById('propShaftFrom') as HTMLSelectElement | null;
      const st = document.getElementById('propShaftTo') as HTMLSelectElement | null;
      const apply = () => {
        if (!sf || !st) return;
        (App.Interaction as any).setShaftRange(shaft, +sf.value, +st.value);
      };
      if (sf) sf.onchange = apply;
      if (st) st.onchange = apply;
      const ssy = document.getElementById('propSystem') as HTMLSelectElement | null;
      if (ssy) ssy.onchange = (ev: Event) =>
        (App.Interaction as any).setShaftSystem(shaft, +(ev.target as HTMLSelectElement).value);
    } else {
      const c = o.cell;
      const t = f?.type[c.x][c.z];
      const tname: Record<number, string> = {
        [T.WALK]: '可走管空间',
        [T.FORBID]: '禁行区',
        [T.ROOM]: '设备机房',
        [T.END]: '末端',
        [T.RISER]: '竖井（立管）',
      };

      sec.innerHTML =
        `<div class="propTitle"><span class="swatch" style="background:#3df0ff"></span>格子</div>` +
        this._row('所在楼层', f?.name || '—') +
        this._row('坐标', `${c.x}, ${c.z}`) +
        this._row('类型', tname[t!] || String(t)) +
        this._row('被走管线覆盖', f?.drawnCells.has(c.x + ',' + c.z) ? '是' : '否');
    }
  },

  /**
   * 生成属性行HTML
   */
  _row(k: string, v: string, isHtml = false): string {
    return `<div class="propRow"><span class="k">${k}</span><span class="v">${isHtml ? v : v}</span></div>`;
  },

  /**
   * 生成管线系统下拉框HTML
   */
  _systemSelect(systemId: number): string {
    const opts = App.Store.systems
      .map((s: any) => `<option value="${s.id}"${s.id === systemId ? ' selected' : ''}>${s.name}</option>`)
      .join('');
    return `<select id="propSystem" style="width:110px;background:#1a2440;color:var(--txt);border:1px solid var(--line);border-radius:5px;padding:3px">${opts}</select>`;
  },

  /**
   * 生成系统简称备注HTML（如 "(水)"），无简称则空
   */
  _abbr(systemId: number): string {
    const a = (App.Routing as any).systemAbbrOf(systemId);
    return a ? '<span style="color:var(--mut);font-weight:400">(' + a + ')</span>' : '';
  },
};

export default App.Selection;
export const Selection = App.Selection;
